/**
 * La conversazione con l'assistente, nel browser e basta.
 *
 * Vive in `sessionStorage`: sopravvive alla navigazione fra le pagine del
 * back-office, muore con la scheda. Il server non la conserva — la riceve
 * ogni volta nel corpo della richiesta — e cosi' non c'e' nulla da cancellare
 * ne' da proteggere lato API. Il limite di ottanta voci evita che una sessione
 * lunga spedisca al modello piu' di quanto il server comunque troncherebbe.
 */

export type Voce =
  | { tipo: "utente"; testo: string }
  | { tipo: "assistente"; testo: string; saluto?: boolean; troncata?: boolean }
  | { tipo: "pagina"; titolo: string }
  | { tipo: "ricominciata" };

export interface MessaggioApi {
  ruolo: "utente" | "assistente";
  contenuto: string;
}

export interface Storia {
  leggi(): Voce[];
  aggiungi(voce: Voce): void;
  sostituisciUltima(voce: Voce): void;
  svuota(): void;
}

export const MASSIMO_VOCI = 80;

/**
 * Una storia legata a una chiave; la memoria e' iniettabile per i test e per
 * sopravvivere a un browser che nega `sessionStorage` (modalita' privata, in
 * cui si prosegue in memoria e la conversazione dura quanto la pagina).
 */
export function creaStoria(chiave: string, memoria: Storage | null): Storia {
  let cache: Voce[] | null = null;

  const carica = (): Voce[] => {
    if (cache) return cache;
    try {
      const grezzo = memoria?.getItem(chiave);
      const letto: unknown = grezzo ? JSON.parse(grezzo) : [];
      // Una chiave lasciata da un'altra versione o manomessa non deve far
      // cadere il widget, e con lui il back-office: si riparte da zero.
      cache = Array.isArray(letto) ? (letto as Voce[]) : [];
    } catch {
      cache = [];
    }
    return cache;
  };

  const salva = (voci: Voce[]): void => {
    cache = voci.slice(-MASSIMO_VOCI);
    try {
      memoria?.setItem(chiave, JSON.stringify(cache));
    } catch {
      // Memoria negata: si resta in cache per questa pagina.
    }
  };

  return {
    leggi: () => [...carica()],
    aggiungi: (voce) => salva([...carica(), voce]),
    sostituisciUltima: (voce) => {
      const voci = carica();
      salva(voci.length ? [...voci.slice(0, -1), voce] : [voce]);
    },
    svuota: () => {
      cache = [];
      try {
        memoria?.removeItem(chiave);
      } catch {
        // Idem.
      }
    },
  };
}

/**
 * I messaggi da mandare all'API: gli ultimi `turni` scambi prima della domanda
 * finale, piu' la domanda. Divisori di pagina, saluti e risposte troncate non
 * sono turni: un saluto e' testo nostro, non del modello, e una risposta a
 * meta' rimandata come contesto farebbe credere al modello di aver detto cose
 * che non ha detto. Dopo una ripartenza («Ricomincia e rifai la domanda»)
 * conta solo quello che la segue: il motivo per ripartire e' proprio non
 * rimandare una risposta che il filtro di sicurezza del sito rifiuta.
 */
export function ultimiTurni(voci: Voce[], turni: number): MessaggioApi[] {
  const ripartenza = voci.map((v) => v.tipo).lastIndexOf("ricominciata");
  const messaggi: MessaggioApi[] = [];
  for (const voce of voci.slice(ripartenza + 1)) {
    if (voce.tipo === "utente") messaggi.push({ ruolo: "utente", contenuto: voce.testo });
    else if (voce.tipo === "assistente" && !voce.saluto && !voce.troncata)
      messaggi.push({ ruolo: "assistente", contenuto: voce.testo });
  }
  if (messaggi.length === 0) return [];
  const domanda = messaggi[messaggi.length - 1];
  const precedenti = messaggi.slice(0, -1);
  return [...(turni > 0 ? precedenti.slice(-2 * turni) : []), domanda];
}
